import { getAvatarColor } from "@/lib/utils/avatar";
import { useSidebarResize, MIN_WIDTH, MIN_SECTION_HEIGHT, MAX_SECTION_HEIGHT } from "./hooks/useSidebarResize";
import { useFolderTree } from "./hooks/useFolderTree";
import { useDraftRename } from "./hooks/useDraftRename";
import { useFileImport } from "./hooks/useFileImport";
import { useDragAndDrop } from "./hooks/useDragAndDrop";
import { useSidebarAccount } from "./hooks/useSidebarAccount";
import { CollapsedSidebar } from "./CollapsedSidebar";
import { SidebarHeader } from "./SidebarHeader";
import { DraftsList } from "./DraftsList";
import { PublishedSharesSection } from "./PublishedSharesSection";
import { ArchiveSections } from "./ArchiveSections";
import { Box, useColorMode, Flex, Input } from "@chakra-ui/react";
import { type Draft } from "@/lib/storage/local-storage";
import type { ShareFormat } from "@core/types/share";
import { type UsageInfo } from "@/lib/api/keys";
import { LoadUrlModal } from "./LoadUrlModal";
import { SignUpModal } from "./SignUpModal";
import { SignInModal } from "./SignInModal";
import { ShareDetailsModal } from "./ShareDetailsModal";
import { SidebarUserMenu } from "./SidebarUserMenu";

interface EditorSidebarProps {
  onSelectFile: (shareId: string) => void;
  onNewFile: () => void;
  onContentLoaded: (
    content: string,
    filename?: string,
    format?: ShareFormat,
  ) => void;
  onCloneShare?: (shareId: string) => void;
  selectedFileId?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  // Draft tracking
  drafts: Draft[];
  currentDraftId: string | null;
  onSelectDraft: (draftId: string) => void;
  onDeleteDraft: (draftId: string) => void;
  onRenameDraft: (draftId: string, newFilename: string) => void;
  onShareDraft?: () => void;
  onSettings?: () => void;
  // Storage usage callback
  onUsageChange?: (usage: UsageInfo | null) => void;
}

export function EditorSidebar({
  onSelectFile,
  onNewFile,
  onContentLoaded,
  onCloneShare,
  selectedFileId,
  isCollapsed,
  onToggleCollapse,
  drafts,
  currentDraftId,
  onSelectDraft,
  onDeleteDraft,
  onRenameDraft,
  onShareDraft,
  onSettings,
  onUsageChange,
}: EditorSidebarProps) {
  const { colorMode } = useColorMode();
  const {
    sidebarWidth, draftsHeight, isResizing,
    handleMouseDown, handleSectionMouseDown,
    sidebarRef, draftsSectionRef,
  } = useSidebarResize();
  const account = useSidebarAccount({ onUsageChange });
  const {
    shares, isLoading, hasApiKey, apiKey, accountName, usage, accountExpiresAt,
    loadShares, handleCopyLink, handleRevoke, handleViewDetails,
    isCopied, getPasscode, getShareUrl, getDisplayName, isEncrypted,
    isDetailsOpen, onDetailsClose, selectedShareForDetails,
    isSignUpOpen, onSignUpOpen,
    signUpUsername, setSignUpUsername, signUpUsernameError, setSignUpUsernameError,
    isSigningUp, generatedKey, acceptedTerms, setAcceptedTerms, termsError, setTermsError,
    handleSignUp, handleSignUpComplete, handleSignUpCancel,
    isSignInOpen, onSignInOpen,
    signInKey, setSignInKey, signInError, setSignInError, isSigningIn,
    handleSignIn, handleSignInCancel,
    handleSignOutComplete,
  } = account;
  const {
    fileInputRef, handleFileUpload, handleFileChange,
    url, setUrl, isLoadingUrl,
    isUrlOpen, onUrlOpen, onUrlClose, handleUrlSubmit,
  } = useFileImport({ onContentLoaded });

  // Folder tree
  const {
    expandedFolders, toggleFolder,
    preservedFolders, setPreservedFolders, deletePreservedFolder,
  } = useFolderTree();

  // Drag-and-drop
  const {
    draggingShareId, setDraggingShareId,
    dragOverFolderPath, setDragOverFolderPath,
    dragImageRef, handleDragEnd,
  } = useDragAndDrop();

  // Draft rename
  const {
    renamingDraftId, renameValue, setRenameValue, renameInputRef,
    handleStartRename, handleConfirmRename, handleRenameKeyDown,
  } = useDraftRename(onRenameDraft);


  // Hidden file input (always rendered)
  const hiddenFileInput = (
    <Input
      ref={fileInputRef}
      type="file"
      accept=".md,.mdc,.markdown"
      display="none"
      onChange={handleFileChange}
    />
  );

  // URL Modal
  const urlModal = (
    <LoadUrlModal
      isOpen={isUrlOpen}
      onClose={onUrlClose}
      url={url}
      setUrl={setUrl}
      onSubmit={handleUrlSubmit}
      isLoading={isLoadingUrl}
    />
  );

  // Sign Up Modal
  const signUpModal = (
    <SignUpModal
      isOpen={isSignUpOpen}
      onCancel={handleSignUpCancel}
      onSignUp={handleSignUp}
      onComplete={handleSignUpComplete}
      signUpUsername={signUpUsername}
      setSignUpUsername={setSignUpUsername}
      signUpUsernameError={signUpUsernameError}
      setSignUpUsernameError={setSignUpUsernameError}
      isSigningUp={isSigningUp}
      generatedKey={generatedKey}
      acceptedTerms={acceptedTerms}
      setAcceptedTerms={setAcceptedTerms}
      termsError={termsError}
      setTermsError={setTermsError}
    />
  );

  // Sign In Modal
  const signInModal = (
    <SignInModal
      isOpen={isSignInOpen}
      onCancel={handleSignInCancel}
      onSignIn={handleSignIn}
      onSwitchToSignUp={() => {
        handleSignInCancel();
        onSignUpOpen();
      }}
      signInKey={signInKey}
      setSignInKey={setSignInKey}
      signInError={signInError}
      setSignInError={setSignInError}
      isSigningIn={isSigningIn}
    />
  );

  // Share Details Modal
  const shareDetailsModal = (
    <ShareDetailsModal
      isOpen={isDetailsOpen}
      onClose={onDetailsClose}
      share={selectedShareForDetails}
      getDisplayName={getDisplayName}
      isEncrypted={isEncrypted}
      getPasscode={getPasscode}
    />
  );

  // User menu component
  const userMenu = (
    <SidebarUserMenu
      hasApiKey={hasApiKey}
      isCollapsed={isCollapsed}
      displayName={accountName || "Anonymous"}
      avatarColor={getAvatarColor(apiKey)}
      usage={usage}
      accountExpiresAt={accountExpiresAt}
      onSignUpOpen={onSignUpOpen}
      onSignInOpen={onSignInOpen}
      onSignOutComplete={handleSignOutComplete}
    />
  );

  const collapsedContent = (
    <CollapsedSidebar
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      onNewFile={onNewFile}
      onSettings={onSettings}
      handleFileUpload={handleFileUpload}
      onUrlOpen={onUrlOpen}
      userMenu={userMenu}
    />
  );

  return (
    <>
      {hiddenFileInput}
      {urlModal}
      {signUpModal}
      {signInModal}
      {shareDetailsModal}
      <Flex direction="row" flexShrink={0} h="full">
        <Flex
          direction="column"
          position="relative"
          w={isCollapsed ? "48px" : `${sidebarWidth}px`}
          minW={isCollapsed ? "48px" : `${MIN_WIDTH}px`}
          maxW={isCollapsed ? "48px" : "50vw"}
          h="full"
          transition={
            isResizing
              ? "none"
              : "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease"
          }
          borderRightWidth="1px"
          borderColor={colorMode === "dark" ? "stone.700" : "stone.200"}
          overflow="hidden"
          flexShrink={0}
        >
          {collapsedContent}
          <Box
            ref={sidebarRef}
            flex={1}
            bg={colorMode === "dark" ? "#0c0a09" : "white"}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            opacity={isCollapsed ? 0 : 1}
            pointerEvents={isCollapsed ? "none" : "auto"}
            transition="opacity 0.2s ease"
          >
            <SidebarHeader
              onToggleCollapse={onToggleCollapse}
              onNewFile={onNewFile}
              handleFileUpload={handleFileUpload}
              onUrlOpen={onUrlOpen}
              onSettings={onSettings}
              hasApiKey={hasApiKey}
              isLoading={isLoading}
              loadShares={loadShares}
            />

            {/* Scrollable content area */}
            <Flex direction="column" flex={1} overflow="hidden" minH={0}>
              <DraftsList
                drafts={drafts}
                currentDraftId={currentDraftId}
                selectedFileId={selectedFileId}
                onSelectDraft={onSelectDraft}
                onDeleteDraft={onDeleteDraft}
                onShareDraft={onShareDraft}
                renamingDraftId={renamingDraftId}
                renameValue={renameValue}
                setRenameValue={setRenameValue}
                renameInputRef={renameInputRef}
                handleStartRename={handleStartRename}
                handleConfirmRename={handleConfirmRename}
                handleRenameKeyDown={handleRenameKeyDown}
                draftsHeight={draftsHeight}
                minSectionHeight={MIN_SECTION_HEIGHT}
                maxSectionHeight={MAX_SECTION_HEIGHT}
                draftsSectionRef={draftsSectionRef}
                hasActiveShares={shares.filter((s) => s.status === "active").length > 0}
              />

              <PublishedSharesSection
                activeShares={shares.filter((s) => s.status === "active")}
                selectedFileId={selectedFileId}
                onSelectFile={onSelectFile}
                onCloneShare={onCloneShare}
                apiKey={apiKey}
                loadShares={loadShares}
                handleCopyLink={handleCopyLink}
                handleRevoke={handleRevoke}
                handleViewDetails={handleViewDetails}
                isCopied={isCopied}
                getShareUrl={getShareUrl}
                getDisplayName={getDisplayName}
                isEncrypted={isEncrypted}
                preservedFolders={preservedFolders}
                setPreservedFolders={setPreservedFolders}
                expandedFolders={expandedFolders}
                toggleFolder={toggleFolder}
                deletePreservedFolder={deletePreservedFolder}
                draggingShareId={draggingShareId}
                setDraggingShareId={setDraggingShareId}
                dragOverFolderPath={dragOverFolderPath}
                setDragOverFolderPath={setDragOverFolderPath}
                dragImageRef={dragImageRef}
                handleDragEnd={handleDragEnd}
                hasDrafts={drafts.length > 0}
                handleSectionMouseDown={handleSectionMouseDown}
              />
            </Flex>

            <ArchiveSections
              shares={shares}
              handleViewDetails={handleViewDetails}
              getDisplayName={getDisplayName}
            />

            {/* User section at bottom */}
            <Box
              px={2}
              py={2}
              borderTopWidth="1px"
              borderColor={colorMode === "dark" ? "stone.700" : "stone.200"}
            >
              {userMenu}
            </Box>
          </Box>
        </Flex>

        {/* Resize handle - outside overflow:hidden container so it's not clipped */}
        {!isCollapsed && (
          <Box
            w="3px"
            minW="3px"
            bg={colorMode === "dark" ? "stone.700" : "stone.200"}
            cursor="col-resize"
            onMouseDown={handleMouseDown}
            position="relative"
            transition="all 0.15s"
            _hover={{
              w: "6px",
              minW: "6px",
              bg: colorMode === "dark" ? "stone.600" : "stone.400",
            }}
            _active={{
              w: "6px",
              minW: "6px",
              bg: "brand.500",
            }}
          />
        )}
      </Flex>
    </>
  );
}
